import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import Estimator from "@/components/site/Estimator";
import useSEO from "@/lib/useSEO";

export default function CostSegCalculatorPage() {
  useSEO({
    title: "Cost Segregation Calculator | Estimate Your Tax Savings | IncentTax",
    description:
      "Free cost segregation calculator. Enter your property purchase price, type, and placed-in-service year to estimate first-year depreciation and cash tax savings.",
    canonical: "https://incenttax.com/calculators/cost-segregation",
  });

  return (
    <PageShell testid="cost-seg-calculator-page">
      <section className="bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
          <p className="text-brass uppercase tracking-[0.28em] text-sm mb-6">Free Calculator</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
            Cost Segregation
            <br />
            <span className="italic text-brass">Tax Savings Calculator.</span>
          </h1>
          <p className="mt-6 text-ivory/85 text-lg sm:text-xl leading-relaxed max-w-3xl">
            Estimate the first-year depreciation deduction and cash tax savings a cost
            segregation study could unlock on your commercial or rental property — in seconds.
          </p>
        </div>
      </section>

      <Estimator />

      <section className="py-20 bg-forest-900 text-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Calculator className="text-brass mx-auto mb-6" size={36} strokeWidth={1.5} />
          <h2 className="font-serif text-3xl sm:text-4xl text-ivory leading-tight mb-6">
            Like what you see? <span className="italic text-brass">Let's make it real.</span>
          </h2>
          <p className="text-ivory/80 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            The calculator gives you a ballpark. A formal engineering-based study
            refines this with parcel-level data and IRS-compliant documentation.
          </p>
          <Link
            to="/#contact"
            data-testid="cost-seg-calc-bottom-cta"
            className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium"
          >
            Request a free consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
